import i18n from "i18next";
import { initReactI18next, useTranslation } from "react-i18next";
import {
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  isLanguage,
  type Language,
} from "@/../convex/config/languages";
import en from "@/locales/en";
import fr from "@/locales/fr";

// ============================================================================
// i18n de l'interface
// ============================================================================
//
// La langue vient, par ordre de priorité :
// 1. de `users.language`, appliquée dès que `api.users.me` répond
//    (cf. `useSyncUserLanguage`) ;
// 2. de la copie en localStorage, pour que la page s'affiche directement dans
//    la bonne langue au rechargement, avant que Convex ait répondu — et sur
//    /signin, où il n'y a pas encore d'utilisateur ;
// 3. de la langue du navigateur ;
// 4. de l'anglais.

export const resources = { en, fr } as const;

export const NAMESPACES = Object.keys(en) as (keyof typeof en)[];

const STORAGE_KEY = "nolenor.language";

function readStoredLanguage(): Language | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isLanguage(stored) ? stored : null;
  } catch {
    return null;
  }
}

function detectBrowserLanguage(): Language | null {
  try {
    for (const tag of navigator.languages ?? [navigator.language]) {
      const base = tag.toLowerCase().split("-")[0];
      if (isLanguage(base)) return base;
    }
  } catch {
    // Pas de `navigator` (tests, SSR) : on retombe sur le défaut.
  }
  return null;
}

function initialLanguage(): Language {
  return readStoredLanguage() ?? detectBrowserLanguage() ?? DEFAULT_LANGUAGE;
}

function reflectOnDocument(language: Language) {
  try {
    document.documentElement.lang = language;
  } catch {
    // Pas de DOM.
  }
}

void i18n.use(initReactI18next).init({
  resources,
  lng: initialLanguage(),
  fallbackLng: DEFAULT_LANGUAGE,
  supportedLngs: SUPPORTED_LANGUAGES,
  ns: NAMESPACES,
  defaultNS: "common",
  interpolation: {
    // React échappe déjà tout ce qu'il rend.
    escapeValue: false,
  },
  returnNull: false,
  // Ressources embarquées : l'init est synchrone, pas de Suspense à prévoir.
  initAsync: false,
  react: { useSuspense: false },
});

reflectOnDocument(currentLanguage());

/** La langue active de l'interface. */
export function currentLanguage(): Language {
  return isLanguage(i18n.resolvedLanguage)
    ? i18n.resolvedLanguage
    : DEFAULT_LANGUAGE;
}

/**
 * Bascule l'interface dans `language` et la retient pour ce navigateur. Ne
 * touche pas au compte : c'est `api.users.updateLanguage` qui l'enregistre.
 */
export function applyLanguage(language: Language) {
  try {
    localStorage.setItem(STORAGE_KEY, language);
  } catch {
    // Stockage indisponible (navigation privée) : la langue vaut pour la session.
  }
  reflectOnDocument(language);
  if (i18n.language !== language) void i18n.changeLanguage(language);
}

/** La langue active, re-rendue à chaque changement. */
export function useLanguage(): Language {
  const { i18n: instance } = useTranslation();
  return isLanguage(instance.resolvedLanguage)
    ? instance.resolvedLanguage
    : DEFAULT_LANGUAGE;
}

/**
 * Locale BCP 47 à passer à `Intl.*` / `toLocaleString` pour la langue active.
 */
export function intlLocale(language: Language = currentLanguage()): string {
  return language === "fr" ? "fr-FR" : "en-US";
}

export const LANGUAGE_LABELS: Record<Language, string> = {
  // Chaque langue s'écrit dans sa propre langue : c'est ce qu'on cherche des
  // yeux quand l'interface est dans une langue qu'on ne lit pas.
  en: "English",
  fr: "Français",
};

export { SUPPORTED_LANGUAGES, type Language };

export default i18n;
